import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';
import { Transaction } from '../types/models';
import axios from 'axios';

export interface TransactionFilters {
  start_date?: string;
  end_date?: string;
  status?: 'pending' | 'completed' | 'failed' | 'reversed';
  service_id?: number;
  customer_id?: number;
  payment_method?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface CreateTransactionData {
  service_id: number;
  customer_id: number;
  amount: number;
  payment_method?: string;
}

class TransactionService {
  private baseUrl = '/admin/county/transactions';

  // Get all transactions
  async getTransactions(filters?: TransactionFilters): Promise<ApiResponse<PaginatedResponse<Transaction>>> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.service_id) params.append('service_id', filters.service_id.toString());
    if (filters?.customer_id) params.append('customer_id', filters.customer_id.toString());
    if (filters?.payment_method) params.append('payment_method', filters.payment_method);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());

    return apiClient.get<PaginatedResponse<Transaction>>(`${this.baseUrl}?${params.toString()}`);
  }

  // Get single transaction
  async getTransaction(id: number): Promise<ApiResponse<Transaction>> {
    return apiClient.get<Transaction>(`${this.baseUrl}/${id}`);
  }

  // Create transaction
  async createTransaction(data: CreateTransactionData): Promise<ApiResponse<Transaction>> {
    return apiClient.post<Transaction>(this.baseUrl, data);
  }

  // Process refund
  async refundTransaction(id: number, reason?: string): Promise<ApiResponse<Transaction>> {
    return apiClient.post<Transaction>(`${this.baseUrl}/${id}/refund`, { reason });
  }

  // Get transaction by reference
  async getByReference(reference: string): Promise<ApiResponse<Transaction>> {
    return apiClient.get<Transaction>(`${this.baseUrl}/reference/${reference}`);
  }

  // Get transaction summary statistics
  async getSummary(filters?: TransactionFilters): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.status) params.append('status', filters.status);

    return apiClient.get<any>(`${this.baseUrl}/summary?${params.toString()}`);
  }

  // Export transactions
  async exportTransactions(filters?: TransactionFilters, format: 'csv' | 'excel' | 'pdf' = 'csv'): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.service_id) params.append('service_id', filters.service_id.toString());
    if (filters?.customer_id) params.append('customer_id', filters.customer_id.toString());
    if (filters?.payment_method) params.append('payment_method', filters.payment_method);
    if (filters?.search) params.append('search', filters.search);

    // Use axios directly for blob responses to avoid JSON parsing
    const token = localStorage.getItem('auth_token');
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

    const response = await axios.get(`${baseURL}${this.baseUrl}/export/${format}?${params.toString()}`, {
      responseType: 'blob',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/csv',
      },
    });

    return response.data;
  }
}

export const transactionService = new TransactionService();
export default transactionService;
